import { Stream } from '@engoo/realtime-protocol/stream';
import { SyncState } from 'app/models/rtc/sync/constants';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { Events } from 'app/services/rtc/sync/documentmanagers';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';

import { useMemoOne } from 'app/utils/useMemoOne';
import React, { useContext, useCallback, useEffect } from 'react';
import { PresenceDocumentManagerState, PresenceDocumentSpec } from 'app/models/presence/documentSpec';
import { OrderedMap } from 'immutable';
import { PresenceChange, PresenceState } from 'app/models/presence/reducer';
import { IPresenceAction } from '@engoo/realtime-protocol/presence';
import { useDispatch } from 'react-redux';
import { discoverUnknownParticipants } from 'app/models/rtc/remote';
import { sleep } from 'app/utils/async';
import { BookingState } from 'app/models/rtc/booking/constants';
import { ResponseError } from 'app/constants/ResponseErrors';
import { throwingFunction } from 'app/utils/throwingMethods';
import { getAnonymousDisplayNameById } from 'app/constants/AnonymousDisplayNames';
import { useI18n } from 'app/components/localization';
import GetParticipantName, { GetParticipantNameType } from 'app/components/rtc/GetParticipantName';

export interface IPresenceContext extends Pick<PresenceDocumentManagerState, 'metadata' | 'syncState'> {
  readonly presence: OrderedMap<number, PresenceChange>;

  /**
   * Returns order in which peer joined the call for the first time,
   * useful for determining own color, etc
   */
  getJoinOrder: (participantId: string) => number;
  /**
   * Returns the display name for a given participant
   */
  getParticipantName: (participantId: string) => string;
}

type PresenceActionEvent = { actions: IPresenceAction[], state: PresenceState };

interface IDeps {
  getParticipantNameFromStore: GetParticipantNameType;
}

const DISCOVER_RETRY_WAIT_TIME = 1000;

const getMethods = (joinOrder?: PresenceState['joinOrder']) => {
  const getJoinOrder = (participantId: string) => joinOrder?.get(participantId) ?? -1;

  return { getJoinOrder };
};

export const PresenceContext = React.createContext<IPresenceContext>({
  metadata: undefined,
  syncState: SyncState.closed,
  presence: OrderedMap(),
  getParticipantName: throwingFunction,
  ...getMethods(undefined),
});

const PresenceDocumentProvider: React.FC<IDeps> = ({ children, getParticipantNameFromStore }) => {
  const bookingState = useBookingState();
  const dispatch = useDispatch();
  const { t }  = useI18n();
  const store = useDocumentManager(Stream.Presence, PresenceDocumentSpec);
  const { manager, syncState, documentState: presenceState, metadata, isBlocked } = store;

  const joinOrder = presenceState?.joinOrder;
  const presence = presenceState?.events ?? OrderedMap();
  const participantNames = presenceState?.participantNames;

  const discoverParticipantsWithRetries = useCallback(async (
    actions: IPresenceAction[],
    currentBookingState: BookingState,
    retries: number = 3,
  ) => {
    try {
      const { bookingId, bookingType } = currentBookingState;
      await dispatch(discoverUnknownParticipants(actions, bookingType, bookingId));
    } catch (error) {
      if (error instanceof ResponseError) {
        if (retries > 0) {
          await sleep(DISCOVER_RETRY_WAIT_TIME);
          discoverParticipantsWithRetries(actions, currentBookingState, retries - 1);
          return;
        }
      }
      throw error;
    }
  }, [dispatch]);

  const remoteUpdateHandler = useCallback(async (e: PresenceActionEvent) => {
    if (!bookingState || isBlocked) {
      return;
    }
    return discoverParticipantsWithRetries(e.actions, bookingState);
  }, [bookingState, isBlocked, discoverParticipantsWithRetries]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    manager.addListener(Events.REMOTE_UPDATED, remoteUpdateHandler);

    return () => {
      manager.removeListener(Events.REMOTE_UPDATED, remoteUpdateHandler);
    };
  }, [manager, remoteUpdateHandler]);

  const getParticipantName = useCallback((participantId: string) => {
    const nameData = participantNames?.get(participantId);
    if (!nameData) {
      // If the realtime server was not able to resolve a participant name,
      // get the participant name by checking entities in the redux store as a fallback.
      // In this case the anonymous name is provided by the backend rather than the realtime server.
      return getParticipantNameFromStore(participantId, t);
    }

    if (nameData.type === 'provided') {
      // A user's rank will be greater than one if someone with the same display name has already joined
      // the call. In such cases, the rank will be displayed to distinguish the participants.
      return nameData.rank > 1 ?  `${nameData.name} ${nameData.rank}` : nameData.name;
    }

    return getAnonymousDisplayNameById(t, nameData.id, nameData.rank);
  }, [participantNames, t, getParticipantNameFromStore]);

  const contextValue: IPresenceContext = useMemoOne(
    () => ({
      ...getMethods(joinOrder),
      getParticipantName,
      syncState,
      metadata,
      presence,
    }),
    [joinOrder, syncState, metadata, presence, getParticipantName],
  );

  return (
    <PresenceContext.Provider value={contextValue}>
      {children}
    </PresenceContext.Provider>
  );
};

const PresenceDocumentProviderInjectDeps: React.FC = ({ children }) => (
  <GetParticipantName>{(getParticipantName) => (
    <PresenceDocumentProvider getParticipantNameFromStore={getParticipantName}>
      {children}
    </PresenceDocumentProvider>
  )}</GetParticipantName>
);

export { PresenceDocumentProviderInjectDeps as PresenceDocumentProvider };

export const usePresenceContext = () => useContext(PresenceContext);
